import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Popup from "@/components/common/Popups/Popup";
import { servicesOrchestratorValues } from "@/config/services";
import { PeerConnectionError } from "@/hooks/usePeerConnectionErrors";
import logger from "@/lib/logging/logger";
import { useStore } from "@/store";
import { ABLY_CONFIG } from "../../core";
import { useVisionPresence } from "../hooks";

type PresencePhase = "initial" | "connected" | "disconnected" | "reconnected";

function ReconnectionPopup() {
	const { t } = useTranslation();
	return (
		<Popup
			show
			content={
				<div className="flex flex-col justify-center items-center h-full">
					<div className="w-64 h-16 relative mb-2 shrink-0">
						<img
							src={`${servicesOrchestratorValues.urls.cdn}/shared/peer-connection-errors/paired-devices.svg`}
							alt="peer-connection-success-icon"
							className="w-full h-full object-contain"
						/>
					</div>
					<h4 className="my-6">{t("popup.Devices successfully paired")}</h4>
					<p>{t("popup.You are ready to continue.")}</p>
				</div>
			}
		/>
	);
}

export default function PresenceProvider({ channelName }: { channelName: string }) {
	const error = useStore((state) => state.error);
	const setError = useStore((state) => state.setError);
	const [phase, setPhase] = useState<PresencePhase>("initial");

	const connectionStatus = useVisionPresence(channelName, { shouldManagePresence: true });
	const bothConnected =
		connectionStatus.isInitialCheckComplete &&
		connectionStatus.isEvaluatedDeviceConnected &&
		connectionStatus.isScannerDeviceConnected;

	useEffect(() => {
		if (!connectionStatus.isInitialCheckComplete) return;

		if (!bothConnected) {
			if (phase !== "disconnected") {
				setPhase("disconnected");
			}

			return;
		}

		const shouldClearPeerError = phase === "disconnected" && error instanceof PeerConnectionError;

		if (shouldClearPeerError) {
			logger.info("FLOW", "Peer reconnected, clearing PeerConnectionError popup");
			setError(null);
			setPhase("reconnected");
			return;
		}

		if (phase === "initial" || phase === "disconnected") {
			setPhase("connected");
		}
	}, [bothConnected, connectionStatus.isInitialCheckComplete, error, phase, setError]);

	useEffect(() => {
		if (phase !== "reconnected") return;
		const timer = setTimeout(() => setPhase("connected"), ABLY_CONFIG.reconnectionPopupDurationMs);
		return () => clearTimeout(timer);
	}, [phase]);

	return phase === "reconnected" ? <ReconnectionPopup /> : null;
}
